// Shared wire contract. Billing failures and ordinary permission denials are separate.
export const ACCOUNT_SUSPENDED_CODE = "ERR_ACCOUNT_SUSPENDED";
export const ACCOUNT_SUSPENDED_PATH = "/account-suspended";
const AUTH0_DENIAL = "This account is suspended. Contact support.";

export function isAccountSuspendedBody(body: unknown): boolean {
  if (typeof body === "string") {
    try {
      return isAccountSuspendedBody(JSON.parse(body));
    } catch {
      return false;
    }
  }
  if (!body || typeof body !== "object") return false;
  // Galactus uses ProblemDetails for filters and errors[] for failed Results.
  if ("code" in body && body.code === ACCOUNT_SUSPENDED_CODE) return true;
  if (!("errors" in body) || !Array.isArray(body.errors)) return false;
  return body.errors.some(
    (error: unknown) =>
      error !== null &&
      typeof error === "object" &&
      "code" in error &&
      error.code === ACCOUNT_SUSPENDED_CODE
  );
}

// Auth0 wraps Action denials differently for callbacks and refresh grants.
// Match only our known denial, never render an arbitrary OAuth error description.
export function isAccountSuspendedAuthError(error: unknown, depth = 0): boolean {
  if (!error || typeof error !== "object" || depth > 4) return false;
  const value = error as Record<string, unknown>;
  if (
    [value.code, value.error, value.error_description, value.message].some(
      (field) =>
        typeof field === "string" &&
        (field === ACCOUNT_SUSPENDED_CODE || field.includes(AUTH0_DENIAL))
    )
  )
    return true;
  return isAccountSuspendedAuthError(value.cause, depth + 1);
}

let activeWork = 0;
let redirectPending = false;

/** Read when adopting a refreshed auth seed; API denials use the same hold. */
export function isSuspensionRedirectPending(): boolean {
  return redirectPending;
}

/** Keep a browser call or streamed response alive until it finishes. */
export function deferSuspensionRedirect(): () => void {
  activeWork += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    activeWork -= 1;
    if (activeWork === 0 && redirectPending) redirectSuspendedAccount();
  };
}

export function redirectSuspendedAccount(): void {
  if (typeof window === "undefined") return;
  if (activeWork > 0) {
    redirectPending = true;
    return;
  }
  redirectPending = false;
  if (window.location.pathname !== ACCOUNT_SUSPENDED_PATH) {
    window.location.replace(ACCOUNT_SUSPENDED_PATH);
  }
}

export async function handleAccountSuspendedResponse(response: Response): Promise<boolean> {
  if (response.status !== 403) return false;
  const body: unknown = await response
    .clone()
    .json()
    .catch(() => null);
  if (!isAccountSuspendedBody(body)) return false;
  redirectSuspendedAccount();
  return true;
}
